/**
 * Dashboard filter bar: date-range picker + 7/14/28-day quick buttons +
 * an LLM pill-tab row.
 *
 * Controlled component — owns no state beyond the fetched provider list.
 * The parent (Home.jsx) holds the canonical `{ start, end, ai_model }`
 * shape and refetches the dashboard whenever the user changes anything.
 *
 * The model list is fetched from `/api/ai/providers/` (same call
 * RunPromptModal.jsx/Schedule.jsx already make) instead of a hardcoded
 * array, so a newly-configured provider (e.g. Google AI Overview) shows
 * up here automatically.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { aiProviders } from "../api/endpoints";
import ModelBadge from "./ModelBadge.jsx";

const QUICK_RANGES = [7, 14, 28];

function isoDate(d) {
  // ``YYYY-MM-DD`` in local time — matches what the <input type="date">
  // expects and what the API parses.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function defaultDashboardFilters(days = 28) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return { start: isoDate(start), end: isoDate(end), ai_model: "all" };
}

export function quickRangeFilters(days) {
  return { ...defaultDashboardFilters(days), ai_model: undefined };
}

export default function DashboardFilters({ value, onChange, disabled }) {
  const { t } = useTranslation();
  const { start, end, ai_model } = value;
  const [models, setModels] = useState([]);

  useEffect(() => {
    aiProviders
      .list()
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setModels(list.filter((p) => p.configured).map((p) => p.value));
      })
      .catch(() => setModels(["openai", "anthropic", "gemini"]));
  }, []);

  function setRange(days) {
    onChange({ ...value, ...quickRangeFilters(days), ai_model });
  }

  function setStart(next) {
    if (!next) return;
    // Don't let start drift past end — clamp.
    const safeEnd = next > end ? next : end;
    onChange({ ...value, start: next, end: safeEnd });
  }

  function setEnd(next) {
    if (!next) return;
    const safeStart = next < start ? next : start;
    onChange({ ...value, start: safeStart, end: next });
  }

  function setModel(next) {
    onChange({ ...value, ai_model: next });
  }

  function isActiveRange(days) {
    const expected = defaultDashboardFilters(days);
    return expected.start === start && expected.end === end;
  }

  return (
    <div
      className="flex flex-wrap items-end gap-3 rounded-2xl bg-white border border-slate-200/70 shadow-sm p-4"
      role="region"
      aria-label={t("filters.title")}
    >
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
          {t("filters.from")}
        </label>
        <input
          type="date"
          className="input w-40"
          value={start}
          max={end}
          onChange={(e) => setStart(e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
          {t("filters.to")}
        </label>
        <input
          type="date"
          className="input w-40"
          value={end}
          min={start}
          max={isoDate(new Date())}
          onChange={(e) => setEnd(e.target.value)}
          disabled={disabled}
        />
      </div>

      <div className="flex items-center gap-1">
        {QUICK_RANGES.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setRange(d)}
            disabled={disabled}
            className={[
              "rounded-md px-3 py-2 text-xs ring-1 transition",
              isActiveRange(d)
                ? "bg-brand-100 text-brand-800 ring-brand-300"
                : "bg-white text-slate-700 ring-slate-200 hover:bg-brand-50",
            ].join(" ")}
          >
            {t("filters.lastNDays", { count: d })}
          </button>
        ))}
      </div>

      <div className="flex flex-col ml-auto">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
          {t("filters.llm")}
        </label>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setModel("all")}
            disabled={disabled}
            className={[
              "rounded-full px-3 py-1.5 text-xs font-medium ring-1 transition",
              (ai_model || "all") === "all"
                ? "bg-brand-600 text-white ring-brand-600"
                : "bg-white text-slate-600 ring-slate-300 hover:bg-slate-50",
            ].join(" ")}
          >
            {t("filters.allLlms")}
          </button>
          {models.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setModel(k)}
              disabled={disabled}
              className={[
                "rounded-full pl-1.5 pr-3 py-1 ring-1 transition",
                ai_model === k
                  ? "ring-brand-600 bg-brand-50"
                  : "ring-slate-300 bg-white hover:bg-slate-50",
              ].join(" ")}
            >
              <ModelBadge modelKey={k} size="sm" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
